import { Observable } from "rxjs";

import st from "lib/utils/index";
import { CompanyMemberFullDecorator } from "lib/decorators/index";
import { CompanyMembersRepository, AccountRepository } from "lib/repositories/index";
import { pushRoute } from "modules/app/actions";
import { createResourceInstance, createFormInstance } from "modules/utils/index";

export const OPEN = "settings/EditCompanyMemberModal/OPEN";
export const CLOSE = "settings/EditCompanyMemberModal/CLOSE";

export const LOAD_MEMBER = "settings/EditCompanyMemberModal/LOAD_MEMBER";
export const FETCH_MEMBER = "settings/EditCompanyMemberModal/FETCH_MEMBER";
export const FETCH_MEMBER_START = "settings/EditCompanyMemberModal/FETCH_MEMBER_START";
export const FETCH_MEMBER_SUCCESS = "settings/EditCompanyMemberModal/FETCH_MEMBER_SUCCESS";
export const FETCH_MEMBER_FAILURE = "settings/EditCompanyMemberModal/FETCH_MEMBER_FAILURE";

export const INITIALIZE_MODEL = "settings/EditCompanyMemberModal/INITIALIZE_MODEL";
export const UPDATE_MODEL = "settings/EditCompanyMemberModal/UPDATE_MODEL";
export const VALIDATE = "settings/EditCompanyMemberModal/VALIDATE";
export const SUBMIT = "settings/EditCompanyMemberModal/SUBMIT";
export const FETCH_SUBMIT_START = "settings/EditCompanyMemberModal/FETCH_SUBMIT_START";
export const FETCH_SUBMIT_SUCCESS = "settings/EditCompanyMemberModal/FETCH_SUBMIT_SUCCESS";
export const FETCH_SUBMIT_FAILURE = "settings/EditCompanyMemberModal/FETCH_SUBMIT_FAILURE";

export const REMOVE = "settings/EditCompanyMemberModal/REMOVE";
export const FETCH_REMOVE_START = "settings/EditCompanyMemberModal/FETCH_REMOVE_START";
export const FETCH_REMOVE_SUCCESS = "settings/EditCompanyMemberModal/FETCH_REMOVE_SUCCESS";
export const FETCH_REMOVE_FAILURE = "settings/EditCompanyMemberModal/FETCH_REMOVE_FAILURE";

export const REMIND_PASSWORD = "settings/EditCompanyMemberModal/REMIND_PASSWORD";
export const FETCH_REMIND_PASSWORD_START = "settings/EditCompanyMemberModal/FETCH_REMIND_PASSWORD_START";
export const FETCH_REMIND_PASSWORD_SUCCESS = "settings/EditCompanyMemberModal/FETCH_REMIND_PASSWORD_SUCCESS";
export const FETCH_REMIND_PASSWORD_FAILURE = "settings/EditCompanyMemberModal/FETCH_REMIND_PASSWORD_FAILURE";

export const stateSelector = (state) => state.settings.EditCompanyMemberModal;

const memberInstance = createResourceInstance({
  LOAD: LOAD_MEMBER,
  FETCH: FETCH_MEMBER,
  FETCH_START: FETCH_MEMBER_START,
  FETCH_SUCCESS: FETCH_MEMBER_SUCCESS,
  FETCH_FAILURE: FETCH_MEMBER_FAILURE,
  stateSelector,
  stateKey: "member",
  getModel: ({ memberId }) => {
    return CompanyMembersRepository.find(memberId);
  },
});

const formInstance = createFormInstance({
  INITIALIZE_MODEL,
  UPDATE_MODEL,
  VALIDATE,
  SUBMIT,
  FETCH_SUBMIT_START,
  FETCH_SUBMIT_SUCCESS,
  FETCH_SUBMIT_FAILURE,
  stateSelector,
  validate: ({ form: { model: user } }, errors, { submitValidate }) => {
    if (!user.id) {
      errors.add(`admin_user.full_name`, st.validators.presence(user.full_name));
      errors.add(`admin_user.full_name`, st.validators.length(user.full_name, { minimum: 5, maximum: 100 }));
      errors.add(`admin_user.email`, st.validators.presence(user.email));
    }
  },
  submitModel: ({ form: { model } }) => {
    return model.id ? CompanyMembersRepository.update(model) : CompanyMembersRepository.create(model);
  },
});

const removalInstance = createResourceInstance({
  FETCH: REMOVE,
  FETCH_START: FETCH_REMOVE_START,
  FETCH_SUCCESS: FETCH_REMOVE_SUCCESS,
  FETCH_FAILURE: FETCH_REMOVE_FAILURE,
  stateSelector,
  stateKey: "removal",
  getModel: ({ memberId }) => {
    if (!memberId) {
      return Observable.empty();
    }
    return CompanyMembersRepository.delete(memberId);
  },
});

const remindPasswordInstance = createResourceInstance({
  FETCH: REMIND_PASSWORD,
  FETCH_START: FETCH_REMIND_PASSWORD_START,
  FETCH_SUCCESS: FETCH_REMIND_PASSWORD_SUCCESS,
  FETCH_FAILURE: FETCH_REMIND_PASSWORD_FAILURE,
  stateSelector,
  stateKey: "remindPassword",
  successAlertContent: () =>
    "Na adres e-mail członka Twojej firmy została wysłana wiadomość z linkiem do wygenerowania nowego hasła.",
  getModel: ({ member: { model: member } }) => {
    return AccountRepository.sendResetPasswordInstructions(member.admin_user.email);
  },
});

const initialState = {
  opened: false,
  memberId: null,
  member: memberInstance.initialState,
  form: formInstance.initialState,
  removal: removalInstance.initialState,
  remindPassword: remindPasswordInstance.initialState,
};

const reducers = {
  ...memberInstance.reducersMap,
  ...formInstance.reducersMap,
  ...removalInstance.reducersMap,
  ...remindPasswordInstance.reducersMap,

  [OPEN]: (state, { member }) => ({
    ...initialState,
    opened: true,
    memberId: member.id,
  }),

  [CLOSE]: (state) => ({
    ...state,
    opened: false,
  }),
};

const epics = [
  ...memberInstance.epics,
  ...formInstance.epics,
  ...removalInstance.epics,
  ...remindPasswordInstance.epics,

  (actions$) =>
    actions$
      .ofType(LOAD_MEMBER)
      .merge(actions$.ofType(FETCH_MEMBER_SUCCESS))
      .map(({ model }) => {
        return initializeModel(model);
      }),

  (actions$) => actions$.ofType(FETCH_SUBMIT_SUCCESS).merge(actions$.ofType(FETCH_REMOVE_SUCCESS)).mapTo(close()),
];

export const { initializeModel, updateModel: updateMember, validate, submit } = formInstance.actions;
export const { load: loadMember, fetch: fetchMember } = memberInstance.actions;
export const { fetch: removeMember } = removalInstance.actions;
export const { fetch: remindPassword } = remindPasswordInstance.actions;

export function open(member) {
  if (member === undefined) {
    member = CompanyMemberFullDecorator.build();
  }

  return (dispatch, getState) => {
    const { opened, memberId } = stateSelector(getState());
    if (!(opened && memberId === member.id)) {
      dispatch({ type: OPEN, member });
      if (member.id && !member.type) {
        dispatch(fetchMember());
      } else {
        dispatch(loadMember(member));
      }
    }

    const location = getState().app.location;
    if (location.query.editMember != member.id) {
      dispatch(
        pushRoute({
          ...location,
          query: {
            ...location.query,
            editMember: member.id,
          },
        })
      );
    }
  };
}

export function close() {
  return (dispatch, getState) => {
    const { opened } = stateSelector(getState());
    if (opened) {
      dispatch({ type: CLOSE });
    }

    const location = getState().app.location;
    if (location.query.editMember) {
      dispatch(
        pushRoute({
          ...location,
          query: {
            ...location.query,
            editMember: undefined,
          },
        })
      );
    }
  };
}

export default st.duck.createDuck(initialState, reducers, epics);



// WEBPACK FOOTER //
// ./src/modules/settings/EditCompanyMemberModal/index.js